import { router, useLocalSearchParams } from 'expo-router';
import { useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    H2Regular,
    Input,
    Private,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { formatPhoneNumber } from '../../../common/utils/userUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { captureException } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { useSpinwheelConnect } from '../../hooks/useSpinwheelConnect';
import { useSpinwheelVerify } from '../../hooks/useSpinwheelVerify';

interface SpinwheelVerificationProps {
    onVerificationComplete?: (code: string) => void;
}

const SpinwheelVerification = ({
    onVerificationComplete,
}: SpinwheelVerificationProps) => {
    const { trackViewOnce } = useViewEvents();
    const user = useAuthedUser();
    const { refetch: refetchUser } = useUser();

    // Get spinwheelUserId from router params
    const { spinwheelUserId, phoneNumber } = useLocalSearchParams<{
        spinwheelUserId: string;
        phoneNumber: string;
    }>();

    trackViewOnce(`Onboarding/SpinwheelVerification`);

    const [verificationCode, setVerificationCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const spinwheelVerify = useSpinwheelVerify();
    const { mutate: resendSms, isPending: isResendingSms } =
        useSpinwheelConnect();
    const otpInputRef = useRef<TextInput | null>(null);

    const handleVerify = () => {
        if (verificationCode.length === 6 && spinwheelUserId) {
            setErrorMessage(''); // Clear any previous error
            spinwheelVerify.mutate(
                {
                    body: {
                        spinwheelUserId,
                        otpCode: verificationCode,
                    },
                },
                {
                    onSuccess: async (_) => {
                        onVerificationComplete?.(verificationCode);

                        // Refetch user to get updated spinwheelId
                        if (refetchUser) {
                            await refetchUser();
                        }

                        // Navigate to account verification loading page
                        router.navigate({
                            pathname:
                                '/onboarding/expenses/account-verification',
                            params: {
                                spinwheelUserId,
                            },
                        });
                    },
                    onError: (error: Error) => {
                        captureException(
                            new Error('Spinwheel error: verification failed'),
                            { error },
                        );

                        // Check for incorrect OTP code error
                        const errorCode = (
                            error as Error & { json?: { errorCode?: string } }
                        )?.json?.errorCode;
                        if (errorCode === 'INCORRECT_OTP_CODE') {
                            setErrorMessage(
                                'Code is incorrect. Please try again.',
                            );
                        } else if (errorCode === 'OTP_CODE_EXPIRED') {
                            setErrorMessage(
                                'Code has expired. Please try again.',
                            );
                        } else {
                            setErrorMessage(
                                'Verification failed. Please try again.',
                            );
                        }
                    },
                },
            );
        }
    };

    const handleResendCode = () => {
        const {
            dateOfBirth: userDob,
            phoneNumber: userPhone,
            id: userId,
        } = user;

        if (userDob == null || userPhone == null || userId == null) {
            captureException(
                new Error(
                    'Spinwheel error: error resending Spinwheel code: date of birth, phone number, or user ID is missing',
                ),
                { userDob, userPhone, userId },
            );
            return;
        }

        // Format date of birth to YYYY-MM-DD (remove time part)
        const dateOfBirth = new Date(userDob)
            .toISOString()
            .split('T')[0] as string;

        resendSms(
            {
                body: {
                    dateOfBirth,
                    extUserId: userId,
                    phoneNumber: userPhone,
                },
            },
            {
                onSuccess: (_) => {
                    setVerificationCode('');
                    // TODO: Show success toast
                },
                onError: (error) => {
                    captureException(
                        new Error('Spinwheel error: failed to resend SMS'),
                        { error },
                    );
                    // TODO: Show error toast
                },
            },
        );
    };

    const handleGoBack = () => {
        router.back();
    };

    const isCodeComplete = verificationCode.length === 6;

    return (
        <FullScreenContainer
            header={
                <XStack
                    padding={16}
                    flex={1}
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    <BackButton
                        fallbackRoute="/home"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                </XStack>
            }
            footer={
                <Button
                    text={spinwheelVerify.isPending ? 'Verifying...' : 'Verify'}
                    variant={
                        isCodeComplete
                            ? ButtonVariant.PRIMARY
                            : ButtonVariant.SECONDARY
                    }
                    size={ButtonSize.LARGE}
                    width="full"
                    onPress={handleVerify}
                    disabled={!isCodeComplete || spinwheelVerify.isPending}
                />
            }
        >
            <YStack
                flex={1}
                paddingHorizontal={24}
                paddingTop={16}
                paddingBottom={32}
                justifyContent="space-between"
            >
                <YStack gap={24}>
                    <YStack gap={8}>
                        <H2Regular color="$black">
                            Enter verification code
                        </H2Regular>
                        <BodyMediumRegular color="$gray1000">
                            Please enter the code sent to{' '}
                            {formatPhoneNumber(
                                phoneNumber ?? user.phoneNumber ?? 'your phone',
                                true,
                            )}
                            .
                        </BodyMediumRegular>
                    </YStack>

                    <YStack alignItems="center">
                        <XStack
                            justifyContent="space-between"
                            width="100%"
                            maxWidth={300}
                            gap={8}
                            onPress={() => otpInputRef.current?.focus()}
                        >
                            {Array.from({ length: 6 }, (_, index) => {
                                const digit = verificationCode[index] ?? '';
                                const isDigit = digit !== '';
                                const isCurrentValue =
                                    index === verificationCode.length;
                                const isLastValue = index === 5;
                                const isCodeComplete =
                                    verificationCode.length === 6;
                                const isValueFocused =
                                    isCurrentValue ||
                                    (isLastValue && isCodeComplete);

                                return (
                                    <View
                                        key={index}
                                        width={40}
                                        height={48}
                                        borderWidth={1}
                                        borderRadius={8}
                                        borderColor={
                                            isValueFocused
                                                ? '$black'
                                                : '$gray400'
                                        }
                                        alignItems="center"
                                        justifyContent="center"
                                        backgroundColor="$white"
                                    >
                                        <BodyLargeEmphasized
                                            color={
                                                isDigit ? '$black' : '$gray400'
                                            }
                                            fontSize={18}
                                        >
                                            {isDigit ? digit : '0'}
                                        </BodyLargeEmphasized>
                                    </View>
                                );
                            })}
                        </XStack>

                        <Private>
                            <Input
                                position="absolute"
                                opacity={0}
                                key="otp"
                                value={verificationCode}
                                onChangeText={setVerificationCode}
                                maxLength={6}
                                textContentType="oneTimeCode"
                                keyboardType="number-pad"
                                ref={otpInputRef}
                                autoCapitalize="none"
                                autoCorrect={false}
                            />
                        </Private>

                        {errorMessage ? (
                            <BodyMediumRegular
                                color="$red900"
                                textAlign="left"
                                marginTop={8}
                            >
                                {errorMessage}
                            </BodyMediumRegular>
                        ) : null}
                    </YStack>
                    <XStack justifyContent="center" gap={12}>
                        <BodyMediumRegular>
                            Didn&apos;t receive a message?
                        </BodyMediumRegular>
                        <XStack cursor="pointer">
                            <BodyMediumRegular
                                color="$black"
                                textDecorationLine="underline"
                                onPress={handleResendCode}
                            >
                                {isResendingSms ? 'Sending...' : 'Resend code'}
                            </BodyMediumRegular>
                        </XStack>
                    </XStack>
                </YStack>
            </YStack>
        </FullScreenContainer>
    );
};

export default SpinwheelVerification;
